import { createTheme, Theme } from '@mui/material/styles';

import { FONT_FAMILY, FONT_SIZE, INPUT_SIZE, SECTION_BG, SECTION_BG_DARKER, TAB_HEIGHT, Z_INDEX } from './layout-size';

// eslint-disable-next-line max-len

const customShadows: Record<number, string> = {
  1: '0 5px 20px rgba(0,0,0,0.1)', // Card Shadow
  3: '0 8px 16px rgba(0, 0, 0, 0.15)', // Dropdown shadow
  24: '0 10px 30px rgba(0, 0, 0, 0.2)', // Modal shadow
};

// Create a default theme to get the default shadows
const theme: Theme = createTheme();

// Merge custom shadows with defaults, explicitly typing `index` as `number`
const mergedShadows = theme.shadows.map(
  // Apply custom shadow only for specified indexes
  (shadow: string, index: number): string => customShadows[index] || shadow
);

const INPUT_SIZES = {
  xs: { height: 32, fontSize: FONT_SIZE.xs, paddingX: 12 },
  sm: { height: 36, fontSize: FONT_SIZE.xs, paddingX: 12 },
  md: { height: 40, fontSize: FONT_SIZE.sm, paddingX: 14 },
  lg: { height: 48, fontSize: FONT_SIZE.md, paddingX: 16 },
};

const AUTOCOMPLETE_END_ADORNMENT_SPACE = 32;
const AUTOCOMPLETE_END_ADORNMENT_SPACE_BOTH = 52;
const AUTOCOMPLETE_END_ADORNMENT_SPACE_ONE = 36;

const baseConfigTheme = {
  breakpoints: {
    values: {
      xs: 0,
      sm: 600,
      md: 900,
      lg: 1300, // change here
      xl: 1536,
    },
  },
  typography: {
    fontFamily:
      // eslint-disable-next-line max-len
      FONT_FAMILY,
    htmlFontSize: 16,
    h1: {
      fontFamily: '"Libre Caslon Condensed", Georgia, serif',
      fontSize: '2.25rem',
      fontWeight: 400,
      lineHeight: 1.2,
      letterSpacing: '-0.5px',
    },
    h2: {
      fontFamily: '"Libre Caslon Condensed", Georgia, serif',
      fontSize: '1.88rem',
      fontWeight: 400,
      lineHeight: 1.2,
      letterSpacing: '-0.5px',
    },
    h3: {
      fontFamily: '"Libre Caslon Condensed", Georgia, serif',
      fontSize: '1.5rem',
      fontWeight: 400,
      lineHeight: 1.2,
      letterSpacing: '-0.5px',
    },
    h4: {
      fontSize: '1.25rem',
      fontWeight: 500,
      lineHeight: 1.2,
      letterSpacing: '-0.5px',
    },
    h5: {
      fontSize: '1.13rem',
      fontWeight: 500,
      lineHeight: 1.2,
      letterSpacing: '-0.5px',
    },
    h6: {
      fontSize: '1rem',
      fontWeight: 500,
      lineHeight: 1.2,
      letterSpacing: '-0.5px',
    },

    subtitle1: {
      fontSize: '1.13rem',
      lineHeight: 1.5,
      letterSpacing: '-0.25px',
    },
    subtitle2: {
      fontSize: '1rem',
      lineHeight: 1.5,
      letterSpacing: '-0.25px',
    },
    body1: {
      fontSize: '0.9rem',
      lineHeight: 1.5,
      letterSpacing: '-0.25px',
    },
    body2: {
      fontSize: '0.8rem',
      lineHeight: 1.5,
      letterSpacing: '-0.25px',
    },
    caption: {
      fontSize: '0.75rem',
      lineHeight: 1.1,
      letterSpacing: '0px',
    },
    overline: {
      fontSize: '0.63rem',
      lineHeight: 1.5,
      textTransform: 'none',
      letterSpacing: '0px',
    },
  },
  spacing: 4,
  shadows: mergedShadows,
  components: {
    MuiButton: {
      variants: [
        {
          props: { variant: 'light', color: 'primary' },
          style: {
            backgroundColor: '#EFF3F9',
            color: '#4574BA',
            fontWeight: '500',
            '&:hover': {
              backgroundColor: '#DFE7F3',
            },
          },
        },
        {
          props: { variant: 'light', color: 'success' },
          style: {
            backgroundColor: '#F5FBF7',
            color: '#45BA58',
            fontWeight: '500',
            '&:hover': {
              backgroundColor: '#ECF8EE',
            },
          },
        },
        {
          props: { variant: 'light', color: 'error' },
          style: {
            backgroundColor: '#F5EDEE',
            color: '#B81F2A',
            fontWeight: '500',
            '&:hover': {
              backgroundColor: '#FAD8DB',
            },
          },
        },
        {
          props: { variant: 'light', color: 'warning' },
          style: {
            backgroundColor: '#FCF6E5',
            color: '#B0913B',
            fontWeight: '500',
            '&:hover': {
              backgroundColor: '#F0E9D5',
            },
          },
        },
        {
          props: { variant: 'outlined', color: 'primary' },
          style: {
            border: '1px solid #DFE7F3',
            fontWeight: '500',
            '&:hover': {
              border: '1px solid #DFE7F3',
              backgroundColor: '#EFF3F9',
            },
          },
        },
        {
          props: { variant: 'outlined', color: 'secondary' },
          style: {
            border: `1px solid ${SECTION_BG_DARKER}`,
            fontWeight: '500',
            '&:hover': {
              border: '1px solid #DFE7F3',
              backgroundColor: '#EFF3F9',
            },
          },
        },
        {
          props: { variant: 'outlined', color: 'success' },
          style: {
            border: '1px solid #ECF2EE',
            fontWeight: '500',
            '&:hover': {
              border: '1px solid #ECF2EE',
              backgroundColor: '#F5FBF7',
            },
          },
        },
        {
          props: { variant: 'outlined', color: 'warning' },
          style: {
            border: '1px solid #F8F2E1',
            fontWeight: '500',
            '&:hover': {
              border: '1px solid #F8F2E1',
              backgroundColor: '#FCF6E5',
            },
          },
        },
        {
          props: { variant: 'outlined', color: 'error' },
          style: {
            border: '1px solid #FAD8DB',
            color: '#DA6974',
            fontWeight: '500',
            '&:hover': {
              border: '1px solid #FAD8DB',
              backgroundColor: '#F5EDEE',
            },
          },
        },
      ],
      styleOverrides: {
        root: {
          fontWeight: '500',
          letterSpacing: '-0.33px',
          textTransform: 'initial',
          fontSize: '0.8rem',
          minWidth: 0,
        },
        contained: {
          color: '#fff',
          boxShadow: 'none',
          '&:hover': {
            boxShadow: 'none',
          },
        },

        containedPrimary: {
          backgroundColor: theme.palette.primary.main,
          color: '#fff',
          '&:hover': {
            backgroundColor: theme.palette.primary.dark,
          },
          '&:active': {
            backgroundColor: theme.palette.primary.light,
          },
          '&.Mui-disabled': {
            backgroundColor: '#BFD3F5',
            color: '#fff',
            opacity: 0.6,
          },
        },
        sizeSmall: {
          minHeight: INPUT_SIZE.sm,
          fontSize: FONT_SIZE.xs,
          padding: '4px 0.5rem',
        },
        sizeMedium: {
          minHeight: INPUT_SIZE.md,
          fontSize: FONT_SIZE.sm,
        },
        startIcon: {
          marginRight: '4px',
          lineHeight: '1rem',
        },
        endIcon: {
          marginLeft: '4px',
          lineHeight: '1rem',
        },
        textSecondary: {
          color: '#627178',
        },
      },
    },
    MuiToggleButtonGroup: {
      styleOverrides: {
        root: {
          gap: '4px',
          marginTop: '0 !important',
        },
        grouped: {
          margin: 0,
          border: 'none',
          '&:not(:first-of-type)': {
            borderRadius: '999px',
          },
          '&:first-of-type': {
            borderRadius: '999px',
          },
        },
      },
      variants: [
        {
          props: { color: 'primary' },
          style: {
            '& .MuiToggleButton-root.Mui-selected': {
              background: '#EBF1FC',
              border: '1px solid transparent!important',
              color: '#202541',
              '&:hover': {
                background: '#DCEAF9',
              },
            },
          },
        },
      ],
    },
    MuiToggleButton: {
      styleOverrides: {
        root: {
          borderRadius: '999px',
          border: '1px solid #EDF0F3!important', // gray.main
          background: '#FFFFFF',
          color: '#202541',
          textTransform: 'none',
          fontWeight: 500,
          letterSpacing: '-0.3px',
          padding: '0px 12px',
          minHeight: INPUT_SIZES.xs.height,
          fontSize: FONT_SIZE.xs, // 18px
          lineHeight: 1.2,
          '&:hover': {
            background: '#EDF0F3', // grayLight.light
          },
          '&.Mui-selected': {
            background: '#202541',
            border: '1px solid #202541!important',
            color: '#fff',
            '&:hover': {
              background: '#202541',
            },
          },
        },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          color: '#7F8D93',
        },
        colorWarning: {
          color: '#DABC69',
        },
        colorSuccess: {
          color: '#45BA58',
        },
        colorError: {
          color: '#DA6974',
        },
        colorInfo: {
          color: '#4EB9D4',
        },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          fontSize: FONT_SIZE.xs,
          fontWeight: '500',
          padding: '4px',
        },
      },
    },
    MuiBadge: {
      styleOverrides: {
        badge: {
          padding: 0,
          minWidth: '16px',
          height: '16px',
          fontWeight: '500',
          fontSize: '10px',
        },
      },
    },
    MuiSwitch: {
      styleOverrides: {
        root: {
          width: '40px',
          height: '22px',
          padding: 0,
          margin: '0 8px',
          display: 'inline-flex',
          alignItems: 'center',
          '& .MuiSwitch-switchBase': {
            padding: '2px',
            transitionDuration: '180ms',
            '&.Mui-checked': {
              transform: 'translateX(18px)',
              color: '#fff',
              '& + .MuiSwitch-track': {
                opacity: 1,
                backgroundColor: theme.palette.primary.main,
              },
            },
          },
          '& .MuiSwitch-thumb': {
            width: '18px',
            height: '18px',
            backgroundColor: '#fff',
            boxShadow: '0 1px 2px rgba(0,0,0,0.12)',
          },
          '& .MuiSwitch-track': {
            height: '100%',
            borderRadius: '999px',
            opacity: 1,
            backgroundColor: '#E7EBEF',
            boxSizing: 'border-box',
          },
        },
      },
    },

    MuiRadio: {
      styleOverrides: {
        root: {
          padding: '0.33rem',
          '& .MuiSvgIcon-root': {
            height: '1rem',
            width: '1rem',
          },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          padding: '6px 0',
          fontWeight: '500',
          height: '30.5px',
          letterSpacing: 0,
          background: '#F4F7FB',
          fontSize: FONT_SIZE.xs,
          '&.MuiChip-clickableColorError:hover': {
            background: '#E9BDC1',
          },
          '&.MuiChip-clickableColorPrimary:hover': {
            background: '#D0E1FB',
          },
          '&.MuiChip-clickableColorWarning:hover': {
            background: '#ECE1C2',
          },
          '&.MuiChip-clickableColorSuccess:hover': {
            background: '#D9F1DD',
          },
        },
        label: {
          display: 'flex',
          alignItems: 'center',
        },
        sizeSmall: {
          height: `${INPUT_SIZE.xs}!important`,
          fontSize: FONT_SIZE.xs,
        },
        colorPrimary: {
          background: '#EFF3F9',
          color: '#3B639E',
          '& svg': {
            color: '#3B639E!important',
          },
        },
        colorSuccess: {
          background: '#ECF8EE',
          color: '#3B9E4B',
          '& svg': {
            color: '#3B9E4B!important',
          },
        },
        colorWarning: {
          background: '#F8F2E1',
          color: '#B49642',
          '& svg': {
            color: '#B49642!important',
          },
        },
        colorError: {
          background: '#F5EDEE',
          color: '#B81F2A',
          '& svg': {
            color: '#B81F2A!important',
          },
        },
        colorSecondary: {
          background: '#F9FAFB',
          color: '#627178',
          '& svg': {
            color: '#627178 !important',
          },
        },
        outlined: {
          background: 'none',
          borderColor: '#f2f4fb',
          '& svg': {
            fontSize: `${FONT_SIZE.sm}!important`,
          },
        },
        outlinedPrimary: {
          '& svg': {
            color: '#6189C4 !important',
          },
        },
        outlinedSecondary: {
          color: '#627178',
          '& svg': {
            color: '#627178 !important',
          },
        },
        outlinedError: {
          border: '1px solid #FAD8DB',
        },
      },
    },
    MuiSvgIcon: {
      styleOverrides: {
        root: {
          width: '0.8em',
          height: '0.8em',
        },
      },
    },
    MuiTabs: {
      styleOverrides: {
        root: {
          minHeight: TAB_HEIGHT,
        },
        indicator: {
          height: '1.5px',
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          textTransform: 'capitalize',
          fontWeight: '500',
          color: '#627178',
          fontSize: FONT_SIZE.sm,
          opacity: 1,
          padding: '6px 8px',
          minWidth: '0 !important',
          minHeight: TAB_HEIGHT,
          letterSpacing: '-0.5px',
          '&:hover': {
            color: '#202541',
          },
          '&.Mui-selected': {
            color: '#2355D3',
          },
          button: {
            opacity: 0,
            transition: 'opacity 0.4s ease, visibility 0s 0.2s',
          },
          '&:hover button': {
            opacity: 1,
          },
        },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          letterSpacing: 0,
          fontSize: FONT_SIZE.sm,
          px: 3,
          py: 2,
        },
      },
    },
    MuiListItemIcon: {
      styleOverrides: {
        root: {
          minWidth: '32px!important',
        },
      },
    },
    MuiAlert: {
      styleOverrides: {
        standardInfo: {
          backgroundColor: '#DAE3F1 !important',
          color: '#3B639E',
        },
      },
    },
    MuiAccordion: {
      defaultProps: {
        elevation: 0,
        square: true,
        disableGutters: true,
      },
      styleOverrides: {
        root: {
          boxShadow: 'none',
          backgroundColor: '#fff',
          borderRadius: '8px',
          border: '1px solid #EDF0F3',
          '&:before': { display: 'none' },
          '&.Mui-expanded': { margin: 0, marginBottom: '2rem' },
        },
      },
    },

    MuiAccordionSummary: {
      styleOverrides: {
        root: {
          minHeight: 56,
          alignItems: 'center',
          paddingLeft: 16,
          paddingRight: 16,

          '&.Mui-expanded': {
            minHeight: 56,
            borderBottomWidth: '1px',
            borderBottomColor: '#E7EBEF',
          },
        },

        content: {
          margin: 0,
          alignItems: 'center',
          letterSpacing: '0px',
          fontSize: FONT_SIZE.md,
          fontWeight: 500,
          '&.Mui-expanded': {
            margin: 0,
          },
        },

        expandIconWrapper: {
          transform: 'scale(1.1)',
          '&.Mui-expanded': {
            transform: 'rotate(180deg) scale(1.25)',
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          marginBottom: '0.66rem',
          background: '#fff',
          overflow: 'hidden',
          border: '1px solid #f2f4fb',
          borderRadius: theme.spacing(1),
          boxShadow: 'none',
          transition: 'all 0.2s ease-in-out',
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        outlined: {
          border: '1px solid #EDF0F3',
          boxShadow: 'none!important',
        },
        root: {
          '&.MuiMenu-paper': {
            boxShadow: '0px 4px 8px rgba(0,0,0,0.08)',
          },
        },
      },
    },
    MuiCardContent: {
      styleOverrides: {
        root: {
          padding: '0.7rem 0.9rem',
          paddingBottom: '12px!important',
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          borderBottom: '1px solid #EDF0F3',
        },
      },
    },
    MuiTableSortLabel: {
      styleOverrides: {
        root: {
          padding: '0.5rem 0 !important',
          letterSpacing: '0',
          lineHeight: '100%',
        },
      },
    },
    MuiFormControlLabel: {
      styleOverrides: {
        label: {
          fontSize: FONT_SIZE.xs,
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: {
          fontSize: FONT_SIZE.xs,
        },
      },
    },

    // INPUT COMPONENTS

    MuiTextField: {
      defaultProps: {
        size: 'small',
        variant: 'outlined',
      },
      styleOverrides: {
        root: {
          '& > .Mui-disabled:not(.MuiInputLabel-root)': {
            background: '#F9FAFB',
            border: 0,
            '& input, & select, .MuiSelect-select.Mui-disabled': {
              '-webkit-text-fill-color': '#202541',
            },
            '.Mui-disabled': {
              color: '#202541!important',
              opacity: '1',
            },
            '& svg': {
              display: 'none',
            },
          },
          '& .Mui-disabled fieldset': {
            border: '0px!important',
          },
        },
      },
    },

    MuiInputBase: {
      styleOverrides: {
        root: {
          '&.MuiInputBase-sizeSmall': {
            minHeight: INPUT_SIZES.sm.height,
            fontSize: INPUT_SIZES.sm.fontSize,
          },
          '&.MuiInputBase-sizeMedium': {
            minHeight: INPUT_SIZES.md.height,
            fontSize: INPUT_SIZES.md.fontSize,
          },
          '&.input-lg': {
            minHeight: INPUT_SIZES.lg.height,
            fontSize: INPUT_SIZES.lg.fontSize,
          },
        },

        input: {
          boxSizing: 'border-box',
          display: 'flex',
          alignItems: 'center',
          lineHeight: 'normal',
          padding: `0 ${INPUT_SIZES.sm.paddingX}px`,
          height: INPUT_SIZES.sm.height,

          '&::placeholder': {
            color: '#7F8D93',
            opacity: 1,
          },

          '&.MuiSelect-select': {
            display: 'flex',
            alignItems: 'center',
            lineHeight: 'normal',
          },

          '.MuiInputBase-sizeMedium &': {
            padding: `0 ${INPUT_SIZES.md.paddingX}px`,
            height: INPUT_SIZES.md.height,
          },

          '.input-lg &': {
            padding: `0 ${INPUT_SIZES.lg.paddingX}px`,
            height: INPUT_SIZES.lg.height,
          },
        },
      },
    },

    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          alignItems: 'center',

          '&:not(.Mui-error):hover .MuiOutlinedInput-notchedOutline': {
            borderColor: '#D0E1FB!important',
          },
          '&:not(.Mui-error):active .MuiOutlinedInput-notchedOutline': {
            borderColor: '#D0E1FB!important',
          },
          '&.Mui-error .MuiOutlinedInput-notchedOutline': {
            borderColor: '#F4D3D6!important',
          },
          '&.Mui-disabled .MuiOutlinedInput-notchedOutline': {
            borderColor: '#E7EBEF!important',
          },
        },
        notchedOutline: {
          borderColor: '#E7EBEF',
        },
      },
    },

    MuiSelect: {
      styleOverrides: {
        root: {
          '&.MuiInputBase-sizeSmall': { minHeight: INPUT_SIZES.sm.height },
          '&.MuiInputBase-sizeMedium': { minHeight: INPUT_SIZES.md.height },
          '&.input-lg': { minHeight: INPUT_SIZES.lg.height },
        },

        select: {
          display: 'flex',
          alignItems: 'center',
          boxSizing: 'border-box',
          lineHeight: 'normal',

          padding: `0 ${INPUT_SIZES.sm.paddingX}px`,
          height: INPUT_SIZES.sm.height,

          '&.MuiInputBase-inputSizeMedium': {
            padding: `0 ${INPUT_SIZES.md.paddingX}px`,
            height: INPUT_SIZES.md.height,
          },

          '.input-lg &': {
            padding: `0 ${INPUT_SIZES.lg.paddingX}px`,
            height: INPUT_SIZES.lg.height,
          },
        },

        icon: {
          top: 'calc(50% - .5em)',
          right: 4,
        },
      },
    },

    MuiAutocomplete: {
      defaultProps: {
        size: 'small',
      },
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            paddingRight: AUTOCOMPLETE_END_ADORNMENT_SPACE,
            '&.MuiInputBase-sizeSmall': {
              padding: '0!important',
            },
            '& .MuiChip-label': {
              fontSize: FONT_SIZE.xxs,
            },
          },
          '& .MuiSvgIcon-root': {
            fontSize: '1rem!important',
          },
          '& .MuiAutocomplete-tag': {
            margin: '8px',
            height: '22px',
            backgroundColor: '#E7EBEF',
            fontSize: '1rem',
          },

          '&.MuiAutocomplete-hasPopupIcon .MuiOutlinedInput-root': {
            paddingRight: AUTOCOMPLETE_END_ADORNMENT_SPACE_ONE,
          },
          '&.MuiAutocomplete-hasClearIcon .MuiOutlinedInput-root': {
            paddingRight: AUTOCOMPLETE_END_ADORNMENT_SPACE_ONE,
          },
          '&.MuiAutocomplete-hasPopupIcon.MuiAutocomplete-hasClearIcon .MuiOutlinedInput-root': {
            paddingRight: AUTOCOMPLETE_END_ADORNMENT_SPACE_BOTH,
          },

          '& .MuiAutocomplete-endAdornment': {
            top: '50%',
            transform: 'translateY(-50%)',
            right: 8,
          },
          '& .MuiAutocomplete-popupIndicator': {
            padding: 0,
          },
          '& .MuiAutocomplete-clearIndicator': {
            padding: 0,
          },
        },
      },
    },

    MuiFilledInput: {
      defaultProps: {
        disableUnderline: true,
      },
      styleOverrides: {
        root: {
          backgroundColor: '#F4F6F8',
          border: '2px solid transparent',
          borderRadius: '8px',
          transition: 'all 0.15s ease',
          '&:hover': {
            backgroundColor: '#F4F6F8',
            borderColor: 'rgba(0,0,0,0.1)',
          },
          '&.Mui-focused': {
            backgroundColor: '#F4F6F8',
            borderWidth: '2px',
            borderColor: 'rgba(0,0,0,0.87)',
          },
          '&.Mui-error': {
            borderColor: '#F4D3D6',
          },
          '&.Mui-disabled': {
            backgroundColor: '#F4F6F8',
          },
          '&::before': { display: 'none' },
          '&::after': { display: 'none' },
          '&.MuiInputBase-multiline': {
            paddingTop: '17px',
            paddingBottom: '4px',
          },
          '& .MuiInputLabel-root': {
            fontWeight: 500,
          },
          '& .MuiInputAdornment-root': {
            marginTop: '0!important',
          },
        },
        input: {
          '&.MuiInputBase-inputSizeSmall:not(.MuiInputBase-inputMultiline)': {
            padding: `8px ${INPUT_SIZES.sm.paddingX}px 2px`,
          },
          '&.MuiInputBase-inputSizeMedium:not(.MuiInputBase-inputMultiline)': {
            padding: `10px ${INPUT_SIZES.md.paddingX}px 2px`,
          },
          '&.MuiInputBase-inputMultiline': {
            padding: '0',
          },
        },
      },
    },

    // END INPUT COMPONENTS

    MuiDivider: {
      styleOverrides: {
        root: {
          borderColor: '#f2f5f7',
          fontSize: FONT_SIZE.xs,
        },
      },
    },
    MuiBackdrop: {
      styleOverrides: {
        root: {
          background: 'rgba(42, 63, 103, 0.15)',
          backdropFilter: 'blur(0px)',
        },
      },
    },
    MuiDataGrid: {
      styleOverrides: {
        root: {
          border: 'none',
          '& .MuiDataGrid-container--top [role="row"]': {
            background: '#fff',
          },
          '& .MuiDataGrid-columnHeader': {
            height: '36px',
            padding: '0 5px',
          },
          '& .MuiDataGrid-columnHeader .MuiButtonBase-root': {
            padding: 0,
          },
          '& .MuiDataGrid-columnHeaderTitle': {
            fontSize: '0.65rem',
            fontWeight: '600',
            color: '#627178',
          },
          '& .MuiDataGrid-cell': {
            fontSize: FONT_SIZE.xs,
            borderBottom: '1px solid #EDF0F3',
            padding: '0 5px',
          },
          '& .MuiDataGrid-cell .MuiButtonBase-root.MuiCheckbox-root': {
            padding: '0px',
          },
          '& .MuiDataGrid-row:nth-child(2n)': {
            background: '#fff',
          },
          '& .MuiDataGrid-row.data-grid-row-green': {
            backgroundColor: '#DAF1DE!important',
          },
          '& .MuiDataGrid-row.data-grid-row-red': {
            backgroundColor: '#F6DCDE!important',
          },
          '& .MuiDataGrid-row.data-grid-row-yellow': {
            backgroundColor: '#f6f3dc!important',
          },
          '& .MuiDataGrid-row.data-grid-row-blue': {
            backgroundColor: '#dce1f6!important',
          },
          '& .MuiDataGrid-row:hover': {
            backgroundColor: '#F9FAFB!important',
          },
          '& .MuiDataGrid-cell--pinnedLeft, & .MuiDataGrid-cell--pinnedRight, & .MuiDataGrid-columnHeader--pinnedLeft':
            {
              background: '#fff!important',
              zIndex: 1,
              boxShadow: '4px 0 2px rgba(0,0,0,0.02)',
            },
          '& .MuiDataGrid-filler--pinnedLeft': {
            background: '#fff',
          },
          '& .hoverable-row:hover': {
            backgroundColor: `${SECTION_BG_DARKER} !important`,
            cursor: 'pointer',
          },
        },
      },
    },
    MuiStepper: {
      styleOverrides: {
        root: {
          background: SECTION_BG,
          padding: '0.5rem 1rem',
          borderRadius: '100px',
        },
      },
    },
    MuiStepLabel: {
      styleOverrides: {
        root: {
          '& svg': {
            width: '0.7em!important',
            height: '0.7em!important',
          },
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        root: {
          zIndex: Z_INDEX.md,
        },
      },
    },
  },
};

export default baseConfigTheme;
